<template>
  <div class="container mt-4 mb-5">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="m-0">Firmenverwaltung</h3>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'Invoices' }">
        Zurück zur Rechnungsübersicht
      </router-link>
    </div>

    <div v-if="isReadOnly" class="alert alert-info">
      Sie haben für Firmen und Zahlungsbedingungen nur Lesezugriff.
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-md-7">
            <label class="form-label" for="companySearch">Suche</label>
            <input
              id="companySearch"
              v-model="search"
              type="text"
              class="form-control"
              placeholder="Name, Firmennummer, Stadt ..."
              @keyup.enter="loadCompanies"
            />
          </div>
          <div class="col-md-2">
            <button class="btn btn-outline-secondary w-100" @click="loadCompanies">
              Suchen
            </button>
          </div>
          <div class="col-md-3">
            <button v-if="canWriteCompanies" class="btn btn-primary w-100" @click="openCreateCompany">
              Neue Firma
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="canImportCompanies || canExportCompanies" class="card mb-4">
      <div class="card-body">
        <h5 class="card-title">Import / Export</h5>
        <MasterDataImportPanel v-if="canImportCompanies" kind="company" label="Firmen" :allow-csv="false" @imported="onCompaniesImported" />
        <div v-if="canExportCompanies" class="d-flex gap-2"><button class="btn btn-outline-success" @click="exportCompanies">Exportieren (JSON)</button></div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <h5 class="card-title">Firmen</h5>
        <div class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th>Standard</th>
                <th>Firmennr.</th>
                <th>Name</th>
                <th>Ort</th>
                <th v-if="hasCompanyActions" class="text-end">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="company in companies"
                :key="company.id"
                :class="{ 'table-active': selectedCompanyId === company.id }"
                @click="selectCompany(company)"
                style="cursor: pointer"
              >
                <td>
                  <span v-if="company.isDefault" class="badge text-bg-success">Default</span>
                </td>
                <td>{{ company.companyNumber || "-" }}</td>
                <td>{{ company.name || "-" }}</td>
                <td>{{ formatCity(company) }}</td>
                <td v-if="hasCompanyActions" class="text-end">
                  <button v-if="canWriteCompanies" class="btn btn-sm btn-outline-primary me-2" @click.stop="openEditCompany(company)">Bearbeiten</button>
                  <button v-if="canCopyCompanies" class="btn btn-sm btn-outline-secondary me-2" @click.stop="openCopyCompany(company)">Duplizieren</button>
                  <button v-if="userStore.isSystemAdmin" class="btn btn-sm btn-outline-primary me-2" @click.stop="openFeatureModal(company)">Funktionen</button>
                  <button
                    v-if="canSetDefaultCompany"
                    class="btn btn-sm btn-outline-success me-2"
                    :disabled="company.isDefault"
                    @click.stop="setDefaultCompany(company)"
                  >
                    Als Default
                  </button>
                  <button v-if="canDeleteCompanies" class="btn btn-sm btn-outline-danger" @click.stop="deleteCompany(company)">Löschen</button>
                </td>
              </tr>
              <tr v-if="companies.length === 0">
                <td :colspan="hasCompanyActions ? 5 : 4" class="text-center text-muted py-4">Keine Firmen gefunden.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="selectedCompany" class="card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h5 class="card-title mb-0">Zahlungsbedingungen</h5>
            <div class="text-muted small">Firma: {{ selectedCompany.name }}</div>
          </div>
          <button v-if="canWritePaymentTerms" class="btn btn-outline-primary btn-sm" @click="openCreatePaymentTerm">Neue Zahlungsbedingung</button>
        </div>

        <PaymentTermImportPanel :key="selectedCompanyId" :company-id="selectedCompanyId" :company-name="selectedCompany.name || ''"
          :current-company-id="userStore.currentCompanyId || ''" :can-read="userStore.hasPermission('companies.paymentterms.read')"
          :can-write="canWritePaymentTerms" :can-set-default="canSetDefaultPaymentTerm" @imported="selectCompany(selectedCompany)" />

        <div class="table-responsive mt-3">
          <table class="table table-sm table-striped table-hover">
            <thead>
              <tr>
                <th>Default</th>
                <th>Name</th>
                <th class="text-end">Tage</th>
                <th>Template</th>
                <th v-if="hasPaymentTermActions" class="text-end">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="term in selectedCompany.paymentTerms || []" :key="term.id">
                <td>
                  <span v-if="term.isDefault" class="badge text-bg-success">Default</span>
                </td>
                <td>{{ term.name }}</td>
                <td class="text-end">{{ term.daysUntilDue }}</td>
                <td class="text-truncate" style="max-width: 520px;">{{ term.textTemplate || "-" }}</td>
                <td v-if="hasPaymentTermActions" class="text-end">
                  <button v-if="canWritePaymentTerms" class="btn btn-sm btn-outline-primary me-2" @click="openEditPaymentTerm(term)">Bearbeiten</button>
                  <button v-if="canCopyPaymentTerms" class="btn btn-sm btn-outline-secondary me-2" @click="openCopyPaymentTerm(term)">Duplizieren</button>
                  <button
                    v-if="canSetDefaultPaymentTerm"
                    class="btn btn-sm btn-outline-success me-2"
                    :disabled="term.isDefault"
                    @click="setDefaultPaymentTerm(term)"
                  >
                    Als Default
                  </button>
                  <button v-if="canDeletePaymentTerms" class="btn btn-sm btn-outline-danger" @click="deletePaymentTerm(term)">Löschen</button>
                </td>
              </tr>
              <tr v-if="(selectedCompany.paymentTerms || []).length === 0">
                <td :colspan="hasPaymentTermActions ? 5 : 4" class="text-center text-muted py-3">Keine Zahlungsbedingungen vorhanden.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="showCompanyModal && canWriteCompanies" class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-xl" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ companyIsEdit ? "Firma bearbeiten" : companyIsCopy ? "Firma duplizieren" : "Neue Firma" }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeCompanyModal"></button>
          </div>
          <div class="modal-body">
            <p v-if="companyIsCopy" class="alert alert-info">Name, Adresse, Kontakt, Sprache und Design wurden übernommen. Firmennummer, Kennungen, Steuerdaten, Bankverbindung, Logo und Zahlungsbedingungen werden neu erfasst. Die Kopie wird erst beim Speichern angelegt.</p>
            <div class="row g-3">
              <div class="col-md-3">
                <label class="form-label" for="companyNumber">Firmennummer</label>
                <input id="companyNumber" v-model="companyForm.companyNumber" type="text" class="form-control" />
              </div>
              <div class="col-md-7">
                <label class="form-label" for="companyName">Name *</label>
                <input id="companyName" v-model="companyForm.name" type="text" class="form-control" required />
              </div>
              <div class="col-md-2">
                <div class="form-check mt-4">
                  <input id="companyIsDefault" v-model="companyForm.isDefault" :disabled="!canSetDefaultCompany" type="checkbox" class="form-check-input" />
                  <label class="form-check-label" for="companyIsDefault">Default</label>
                </div>
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-4">
                <label class="form-label" for="companyDefaultTheme">Default Theme</label>
                <select id="companyDefaultTheme" v-model="companyForm.defaultTheme" class="form-select">
                  <option v-for="t in themes" :key="t.id" :value="t.id">{{ t.label }}</option>
                </select>
                <div class="text-muted small mt-1">
                  Wird verwendet, wenn der Benutzer kein eigenes Theme gesetzt hat.
                </div>
              </div>
              <div class="col-md-4">
                <label class="form-label" for="companyExportLocale">Export-Sprache</label>
                <select id="companyExportLocale" v-model="companyForm.exportLocale" class="form-select">
                  <option value="de">Deutsch</option>
                  <option value="en">Englisch</option>
                </select>
                <div class="text-muted small mt-1">
                  Wird für lokalisierte Exporte verwendet (z.B. Länder).
                </div>
              </div>
              <div class="col-md-4">
                <label class="form-label" for="companyLogoFile">Logo</label>
                <div class="input-group">
                  <input
                    id="companyLogoFile"
                    type="file"
                    class="form-control"
                    accept="image/*"
                    @change="onLogoSelected"
                  />
                  <button class="btn btn-outline-primary" type="button" :disabled="!logoFile" @click="uploadLogo">
                    Upload
                  </button>
                  <button class="btn btn-outline-danger" type="button" :disabled="!companyForm.logoFileId" @click="removeLogo">
                    Entfernen
                  </button>
                </div>
                <div class="text-muted small mt-1">
                  Aktuell: <code>{{ companyForm.logoFileId || "-" }}</code>
                </div>
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-3">
                <div class="form-check mt-4">
                  <input id="companyApiQuotaEnabled" v-model="companyForm.apiQuotaEnabled" type="checkbox" class="form-check-input" />
                  <label class="form-check-label" for="companyApiQuotaEnabled">API-Abrechnung aktiv</label>
                </div>
              </div>
              <div class="col-md-4">
                <label class="form-label">API-Plan</label>
                <div class="d-flex gap-2 align-items-center">
                  <div class="form-control bg-light">{{ companyForm.apiQuotaPlanCode || "Flex" }}</div>
                  <button
                    class="btn btn-outline-primary"
                    type="button"
                    :disabled="!companyIsEdit || apiQuotaPlans.length === 0"
                    @click="openApiQuotaPlanChange"
                  >
                    Ändern
                  </button>
                </div>
                <div class="text-muted small mt-1">
                  Preisstaffel und inkludierte Credits werden DB-seitig gepflegt.
                </div>
              </div>
              <div class="col-md-5">
                <label class="form-label" for="companyMaxOverageAmountEurosPerMonth">Max. Überschusskosten pro Monat</label>
                <input
                  id="companyMaxOverageAmountEurosPerMonth"
                  v-model.number="companyForm.maxOverageAmountEurosPerMonth"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control"
                  :disabled="!companyForm.apiQuotaEnabled"
                  placeholder="leer = unbegrenzt"
                />
                <div class="text-muted small mt-1">
                  Betrag in Euro. Firmenadmins können nur dieses Kosten-Cap anpassen.
                </div>
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-12">
                <div class="text-muted small mt-2">
                  Abrechenbar sind erfolgreiche billable API-Funktionen im Kalendermonat Europe/Berlin.
                </div>
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-6">
                <label class="form-label" for="companyStreet">Straße</label>
                <input id="companyStreet" v-model="companyForm.street" type="text" class="form-control" />
              </div>
              <div class="col-md-2">
                <label class="form-label" for="companyPostcode">PLZ</label>
                <input id="companyPostcode" v-model="companyForm.postcode" type="text" class="form-control" />
              </div>
              <div class="col-md-4">
                <label class="form-label" for="companyCity">Stadt</label>
                <input id="companyCity" v-model="companyForm.city" type="text" class="form-control" />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-3">
                <SearchableDropdown
                  input-id="companyCountry"
                  label="Land"
                  :model-value="companyForm.countryCode"
                  @update:modelValue="val => { companyForm.countryCode = val; }"
                  :options="masterDataStore.countryCodes"
                />
              </div>
              <div class="col-md-5">
                <label class="form-label" for="companyElectronicAddress">E-Mail / Electronic Address</label>
                <input id="companyElectronicAddress" v-model="companyForm.electronicAddress" type="text" class="form-control" />
              </div>
              <div class="col-md-4">
                <label class="form-label" for="companyGlobalId">Global ID</label>
                <input id="companyGlobalId" v-model="companyForm.globalID" type="text" class="form-control" />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-4">
                <SearchableDropdown
                  input-id="companyGlobalIdScheme"
                  label="Global ID Scheme"
                  :model-value="companyForm.globalIDSchemeIdentifier"
                  @update:modelValue="val => { companyForm.globalIDSchemeIdentifier = val; }"
                  :options="masterDataStore.globalIdSchemeIdentifiers"
                />
              </div>
              <div class="col-md-4">
                <label class="form-label" for="companyIban">IBAN</label>
                <input id="companyIban" v-model="companyForm.iban" type="text" class="form-control" />
              </div>
              <div class="col-md-4">
                <label class="form-label" for="companyBic">BIC</label>
                <input id="companyBic" v-model="companyForm.bic" type="text" class="form-control" />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-6">
                <label class="form-label" for="companyBankName">Bankname</label>
                <input id="companyBankName" v-model="companyForm.bankName" type="text" class="form-control" />
              </div>
              <div class="col-md-6">
                <label class="form-label" for="companyContactName">Kontaktname</label>
                <input id="companyContactName" v-model="companyForm.contactName" type="text" class="form-control" />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-4">
                <label class="form-label" for="companyContactOrgUnit">Org Unit</label>
                <input id="companyContactOrgUnit" v-model="companyForm.contactOrgUnit" type="text" class="form-control" />
              </div>
              <div class="col-md-4">
                <label class="form-label" for="companyContactEmail">Kontakt E-Mail</label>
                <input id="companyContactEmail" v-model="companyForm.contactEmailAddress" type="text" class="form-control" />
              </div>
              <div class="col-md-4">
                <label class="form-label" for="companyContactPhone">Kontakt Telefon</label>
                <input id="companyContactPhone" v-model="companyForm.contactPhoneNo" type="text" class="form-control" />
              </div>
            </div>

            <div class="mt-4">
              <h5 class="mb-2">Steuerregistrierungen</h5>
              <div v-for="(reg, idx) in companyForm.taxRegistrations" :key="`ctax-${idx}`" class="row g-2 align-items-end mb-2">
                <div class="col-md-7">
                  <label class="form-label" :for="`ctaxRegNo-${idx}`">TaxRegNo</label>
                  <input :id="`ctaxRegNo-${idx}`" v-model="reg.taxRegNo" type="text" class="form-control" />
                </div>
                <div class="col-md-4">
                  <SearchableDropdown
                    :input-id="`ctaxScheme-${idx}`"
                    label="TaxSchemeID"
                    :model-value="reg.taxSchemeID"
                    @update:modelValue="val => { reg.taxSchemeID = val; }"
                    :options="masterDataStore.taxRegistrationSchemeIds"
                  />
                </div>
                <div class="col-md-1 text-end">
              <button class="btn btn-outline-danger" type="button" @click="removeCompanyTaxReg(idx)">X</button>
            </div>
          </div>
              <button class="btn btn-outline-secondary btn-sm" type="button" @click="addCompanyTaxReg">+ Hinzufügen</button>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" type="button" @click="closeCompanyModal">Abbrechen</button>
            <button class="btn btn-primary" type="button" :disabled="!companyForm.name || companySaving" @click="saveCompany()">
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="featureCompany && userStore.isSystemAdmin" class="modal fade show d-block" tabindex="-1" role="dialog" aria-labelledby="companyFeaturesTitle" aria-modal="true">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 id="companyFeaturesTitle" class="modal-title">Firmenfunktionen bearbeiten</h5>
            <button type="button" class="btn-close" aria-label="Schließen" :disabled="featuresSaving" @click="closeFeatureModal"></button>
          </div>
          <div class="modal-body">
            <p class="mb-1"><strong>Firma:</strong> {{ featureCompany.name }}</p>
            <p class="text-muted small">Diese Freigaben gelten zusätzlich zu Lizenz und Benutzerrechten.</p>
            <p v-if="featureError" class="alert alert-danger" role="alert">{{ featureError }}</p>
            <p v-if="featuresLoading" class="text-muted">Freigaben werden geladen …</p>
            <div v-for="feature in featureDefinitions" :key="feature.id" class="form-check mb-2">
              <input :id="`feature-${feature.id}`" v-model="featureDraft[feature.id]" type="checkbox"
                class="form-check-input" :disabled="featuresLoading || featuresSaving || !featureSettings[feature.id]" />
              <label class="form-check-label" :for="`feature-${feature.id}`">{{ feature.label }}</label>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" type="button" :disabled="featuresSaving" @click="closeFeatureModal">Abbrechen</button>
            <button class="btn btn-primary" type="button" :disabled="featuresLoading || featuresSaving || !hasFeatureChanges" @click="saveCompanyFeatures">
              {{ featuresSaving ? "Speichere …" : "Speichern" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showApiQuotaPlanConfirmModal" class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">API-Plan ändern</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="cancelApiQuotaPlanChange"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label" for="apiQuotaPlanChangeTarget">Neuer API-Plan</label>
              <select
                id="apiQuotaPlanChangeTarget"
                v-model="apiQuotaPlanChangeForm.requestedPlanCode"
                class="form-select"
                @change="previewApiQuotaPlanChange"
              >
                <option
                  v-for="plan in apiQuotaPlans"
                  :key="getApiQuotaPlanCode(plan)"
                  :value="getApiQuotaPlanCode(plan)"
                >
                  {{ getApiQuotaPlanCode(plan) }}
                </option>
              </select>
            </div>
            <p class="mb-2">
              API-Plan von <strong>{{ apiQuotaPlanChangePreview?.currentPlanCode || companyForm.originalApiQuotaPlanCode }}</strong> auf
              <strong>{{ apiQuotaPlanChangePreview?.requestedPlanCode || apiQuotaPlanChangeForm.requestedPlanCode }}</strong> ändern?
            </p>
            <p class="mb-2">
              Neuer Preis: <strong>{{ formatApiQuotaPlanChangePrice(apiQuotaPlanChangePreview) }}</strong>
            </p>
            <p v-if="apiQuotaPlanChangePreview?.effectiveAtLocal" class="mb-2 text-muted">
              Wirksam ab: {{ formatApiPlanChangeEffectiveDate(apiQuotaPlanChangePreview) }}
            </p>
            <p v-if="apiQuotaPlanChangePreview?.message" class="mb-2 text-muted">
              {{ apiQuotaPlanChangePreview.message }}
            </p>
            <div v-if="pendingApiQuotaDowngrade" class="alert alert-warning mb-2">
              <div>
                Es gibt bereits einen offenen Downgrade-Prozess auf
                <strong>{{ getPendingDowngradeApiPlan() }}</strong>
                <span v-if="formatPendingDowngradeTimestamp()">
                  seit {{ formatPendingDowngradeTimestamp() }}
                </span>.
              </div>
            </div>
            <p v-if="apiQuotaPlanChangePreview?.willCancelPendingDowngrade" class="mb-2 text-muted">
              Der offene Downgrade-Prozess wird beim Upgrade gelöscht.
            </p>
            <p v-if="apiQuotaPlanChangePreview?.willReplacePendingDowngrade" class="mb-2 text-muted">
              Der offene Downgrade-Prozess wird durch diese Auswahl ersetzt.
            </p>
            <p v-if="apiQuotaPlanChangePreview?.changeKind === 'Upgrade'" class="mb-0 text-muted">
              Diese Änderung kann Kosten verursachen.
            </p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" type="button" @click="cancelApiQuotaPlanChange">Abbrechen</button>
            <button
              class="btn btn-primary"
              type="button"
              :disabled="!apiQuotaPlanChangeForm.requestedPlanCode || apiQuotaPlanChangePreview?.changeKind === 'None'"
              @click="confirmApiQuotaPlanChange"
            >
              Bestätigen
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showPaymentTermModal && canWritePaymentTerms" class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ paymentTermIsEdit ? "Zahlungsbedingung bearbeiten" : paymentTermIsCopy ? "Zahlungsbedingung duplizieren" : "Neue Zahlungsbedingung" }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closePaymentTermModal"></button>
          </div>
          <div class="modal-body">
            <p v-if="paymentTermIsCopy" class="alert alert-info">Die Kopie wird beim Speichern für diese Firma angelegt und ist zunächst keine Standard-Zahlungsbedingung.</p>
            <div class="row g-3">
              <div class="col-md-8">
                <label class="form-label" for="ptName">Name *</label>
                <input id="ptName" v-model="paymentTermForm.name" type="text" class="form-control" required />
              </div>
              <div class="col-md-4">
                <label class="form-label" for="ptDays">Tage bis fällig</label>
                <input id="ptDays" v-model.number="paymentTermForm.daysUntilDue" type="number" min="0" max="3650" class="form-control" />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-12">
                <label class="form-label" for="ptTemplate">TextTemplate</label>
                <textarea
                  id="ptTemplate"
                  v-model="paymentTermForm.textTemplate"
                  class="form-control"
                  rows="3"
                  placeholder="z.B. Zahlbar bis {DUE_DATE} ohne Abzug. ({DAYS} Tage)"
                ></textarea>
                <div class="text-muted small mt-1">
                  Platzhalter: <code>{DUE_DATE}</code> (dd.MM.yyyy), <code>{DAYS}</code> (Tage)
                </div>
              </div>
            </div>

            <div class="form-check mt-3">
              <input id="ptDefault" v-model="paymentTermForm.isDefault" :disabled="!canSetDefaultPaymentTerm" class="form-check-input" type="checkbox" />
              <label class="form-check-label" for="ptDefault">Als Default setzen</label>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" type="button" @click="closePaymentTermModal">Abbrechen</button>
            <button class="btn btn-primary" type="button" :disabled="!paymentTermForm.name || paymentTermSaving" @click="savePaymentTerm">
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MasterDataImportPanel from "@/components/Administration/MasterDataImportPanel.vue";
import apiClient from "@/axiosConfig";
import CompanyService from "@/services/CompanyService";
import FileTemplateService from "@/services/FileTemplateService";
import { useUxStore } from "@/stores/uxStore";
import { useMasterDataStore } from "@/stores/masterDataStore";
import SearchableDropdown from "@/components/Shared/SearchableDropdown.vue";
import PaymentTermImportPanel from "@/components/Administration/PaymentTermImportPanel.vue";
import { THEMES } from "@/themes/themeRegistry";
import { useUserStore } from "@/stores/userStore";
import { useCompanyFeatureStore } from "@/stores/companyFeatureStore";
import { copyCompany, copyPaymentTerm } from "@/utils/masterDataCopy.cjs";

const featureDefinitions = [
  { id: "banking-opos", label: "Bankkonten und offene Posten" },
  { id: "workforce", label: "Arbeitszeit und Urlaub" },
  { id: "billing", label: "Kimai-Abrechnung" },
];

const emptyCompanyForm = () => ({
  id: "",
  companyNumber: "",
  isDefault: false,
  name: "",
  logoFileId: null,
  defaultTheme: "EHW",
  exportLocale: "de",
  apiQuotaEnabled: false,
  apiQuotaPlanCode: "Flex",
  originalApiQuotaPlanCode: "Flex",
  apiQuotaPlanDisplayName: "Flex",
  maxOverageAmountEurosPerMonth: null,
  street: "",
  postcode: "",
  city: "",
  countryCode: "DE",
  globalID: "",
  globalIDSchemeIdentifier: "0",
  electronicAddress: "",
  iban: "",
  bic: "",
  bankName: "",
  contactName: "",
  contactOrgUnit: "",
  contactEmailAddress: "",
  contactPhoneNo: "",
  taxRegistrations: [],
});

const emptyPaymentTermForm = () => ({
  id: "",
  name: "",
  daysUntilDue: 0,
  textTemplate: "",
  isDefault: false,
});

export default {
  name: "CompanyAdminView",
  components: { MasterDataImportPanel, SearchableDropdown, PaymentTermImportPanel },
  data() {
    return {
      companies: [],
      apiQuotaPlans: [],
      search: "",
      selectedCompanyId: "",
      selectedCompany: null,
      themes: THEMES,
      logoFile: null,
      showCompanyModal: false,
      companyIsEdit: false,
      companyIsCopy: false,
      companySaving: false,
      companyForm: emptyCompanyForm(),
      featureCompany: null,
      featureDefinitions,
      featureSettings: {},
      featureDraft: {},
      featuresLoading: false,
      featuresSaving: false,
      featureError: "",
      companyFeatureStore: useCompanyFeatureStore(),
      showApiQuotaPlanConfirmModal: false,
      apiQuotaPlanChangeForm: {
        requestedPlanCode: "Flex",
      },
      apiQuotaPlanChangePreview: null,
      pendingApiQuotaDowngrade: null,
      showPaymentTermModal: false,
      paymentTermIsEdit: false,
      paymentTermIsCopy: false,
      paymentTermSaving: false,
      paymentTermForm: emptyPaymentTermForm(),
      uxStore: useUxStore(),
      masterDataStore: useMasterDataStore(),
      userStore: useUserStore(),
    };
  },
  computed: {
    canWriteCompanies() {
      return this.userStore.hasPermission("companies.write");
    },
    canCopyCompanies() {
      return this.canWriteCompanies && this.userStore.hasPermission("companies.read");
    },
    canDeleteCompanies() {
      return this.userStore.hasPermission("companies.delete");
    },
    canSetDefaultCompany() {
      return this.userStore.hasPermission("companies.setdefault");
    },
    canImportCompanies() {
      return this.userStore.hasPermission("companies.import");
    },
    canExportCompanies() {
      return this.userStore.isSystemAdmin
        && this.userStore.hasPermission("companies.export")
        && this.userStore.hasPermission("files.read");
    },
    canWritePaymentTerms() {
      return this.userStore.hasPermission("companies.paymentterms.write");
    },
    canCopyPaymentTerms() {
      return this.canWritePaymentTerms && this.userStore.hasPermission("companies.paymentterms.read");
    },
    canDeletePaymentTerms() {
      return this.userStore.hasPermission("companies.paymentterms.delete");
    },
    canSetDefaultPaymentTerm() {
      return this.userStore.hasPermission("companies.paymentterms.setdefault");
    },
    hasCompanyActions() {
      return this.canWriteCompanies || this.canDeleteCompanies || this.canSetDefaultCompany || this.userStore.isSystemAdmin;
    },
    hasFeatureChanges() {
      return this.featureDefinitions.some(({ id }) => this.featureSettings[id]
        && this.featureDraft[id] !== this.featureSettings[id].enabled);
    },
    hasPaymentTermActions() {
      return this.canWritePaymentTerms || this.canDeletePaymentTerms || this.canSetDefaultPaymentTerm;
    },
    isReadOnly() {
      return !this.hasCompanyActions
        && !this.hasPaymentTermActions
        && !this.canImportCompanies
        && !this.canExportCompanies;
    },
  },
  methods: {
    formatCity(company) {
      const parts = [];
      if (company.postcode) parts.push(company.postcode);
      if (company.city) parts.push(company.city);
      return parts.length ? parts.join(" ") : "-";
    },
    async loadCompanies() {
      const response = await CompanyService.getAll(this.search || null, 1, 200);
      this.companies = Array.isArray(response?.data) ? response.data : [];

      if (this.selectedCompanyId) {
        const stillExists = this.companies.some((c) => c.id === this.selectedCompanyId);
        if (!stillExists) {
          this.selectedCompanyId = "";
          this.selectedCompany = null;
        }
      }
    },
    async loadApiQuotaPlans() {
      const response = await CompanyService.getApiQuotaPlans();
      this.apiQuotaPlans = Array.isArray(response?.data) ? response.data : [];
      if (this.apiQuotaPlans.length > 0 && !this.apiQuotaPlans.some((p) => p.code === this.companyForm.apiQuotaPlanCode)) {
        this.companyForm.apiQuotaPlanCode = this.apiQuotaPlans[0].code;
      }
    },
    getApiQuotaPlanCode(plan) {
      return plan?.code ?? plan?.Code ?? "";
    },
    getApiQuotaPlan(planCode) {
      const normalizedPlanCode = String(planCode || "").toLowerCase();
      return this.apiQuotaPlans.find((p) => String(this.getApiQuotaPlanCode(p)).toLowerCase() === normalizedPlanCode) || null;
    },
    getApiQuotaPlanSortOrder(planCode) {
      const plan = this.getApiQuotaPlan(planCode);
      const sortOrder = Number(plan?.sortOrder ?? plan?.SortOrder);
      return Number.isFinite(sortOrder) ? sortOrder : null;
    },
    getApiQuotaPlanPrice(planCode) {
      const plan = this.getApiQuotaPlan(planCode);
      const rawPrice = plan?.price ?? plan?.Price;
      if (rawPrice === null || rawPrice === undefined || rawPrice === "") {
        return null;
      }

      const price = typeof rawPrice === "string"
        ? Number(rawPrice.replace("€", "").trim().replace(/\./g, "").replace(",", "."))
        : Number(rawPrice);
      return Number.isFinite(price) ? price : null;
    },
    formatApiQuotaPlanPrice(planCode) {
      const price = this.getApiQuotaPlanPrice(planCode);
      if (price === null) {
        return "Preis nicht verfügbar";
      }

      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(price);
    },
    formatApiQuotaPlanChangePrice(preview) {
      const amount = preview?.requestedMonthlyBasePriceCents;
      if (preview?.isCustomPrice) {
        return "Individuell";
      }
      if (!Number.isFinite(amount)) {
        return this.formatApiQuotaPlanPrice(this.apiQuotaPlanChangeForm.requestedPlanCode);
      }

      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: preview?.currency || "EUR",
      }).format(amount / 100);
    },
    centsToEuros(value) {
      const cents = Number(value);
      return Number.isFinite(cents) ? cents / 100 : null;
    },
    eurosToCents(value) {
      const euros = Number(value);
      return Number.isFinite(euros) ? Math.round(Math.max(0, euros) * 100) : null;
    },
    formatDateTime(rawValue) {
      if (!rawValue) return "";
      const value = new Date(rawValue);
      if (Number.isNaN(value.getTime())) return "";

      return new Intl.DateTimeFormat("de-DE", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(value);
    },
    formatApiPlanChangeEffectiveDate(preview) {
      if (preview?.changeKind === "Upgrade") {
        return "Sofort";
      }

      return this.formatDateTime(preview?.effectiveAtLocal);
    },
    isApiQuotaPlanDowngrade(planCode) {
      if (!this.companyIsEdit) return false;
      const currentSortOrder = this.getApiQuotaPlanSortOrder(this.companyForm.originalApiQuotaPlanCode);
      const targetSortOrder = this.getApiQuotaPlanSortOrder(planCode);
      return Number.isFinite(currentSortOrder) && Number.isFinite(targetSortOrder) && targetSortOrder < currentSortOrder;
    },
    isApiQuotaPlanChanged() {
      return String(this.companyForm.originalApiQuotaPlanCode || "Flex").toLowerCase()
        !== String(this.companyForm.apiQuotaPlanCode || "Flex").toLowerCase();
    },
    getPendingDowngradeApiPlan() {
      return this.pendingApiQuotaDowngrade?.requestedPlanCode
        ?? this.pendingApiQuotaDowngrade?.RequestedPlanCode
        ?? this.pendingApiQuotaDowngrade?.apiPlan
        ?? this.pendingApiQuotaDowngrade?.ApiPlan
        ?? "-";
    },
    formatPendingDowngradeTimestamp() {
      const rawTimestamp = this.pendingApiQuotaDowngrade?.requestedAtUtc
        ?? this.pendingApiQuotaDowngrade?.RequestedAtUtc
        ?? this.pendingApiQuotaDowngrade?.creationTimestamp
        ?? this.pendingApiQuotaDowngrade?.CreationTimestamp;
      return this.formatDateTime(rawTimestamp);
    },
    async loadActivePendingDowngradePlan() {
      this.pendingApiQuotaDowngrade = null;
      if (!this.companyForm.id) return;

      const response = await CompanyService.getActivePendingDowngradePlan(this.companyForm.id);
      this.pendingApiQuotaDowngrade = response?.data || null;
    },
    async selectCompany(company) {
      this.selectedCompanyId = company.id;
      const response = await CompanyService.getById(company.id);
      this.selectedCompany = response?.data || null;
    },
    openCreateCompany() {
      if (!this.canWriteCompanies) return;
      this.companyIsEdit = false;
      this.companyIsCopy = false;
      this.companyForm = emptyCompanyForm();
      this.showCompanyModal = true;
    },
    async openEditCompany(company) {
      if (!this.canWriteCompanies) return;
      this.companyIsEdit = true;
      this.companyIsCopy = false;
      const response = await CompanyService.getById(company.id);
      const full = response?.data || company;
      const base = emptyCompanyForm();
      this.companyForm = {
        ...base,
        ...full,
        logoFileId: full.logoFileId ?? base.logoFileId,
        defaultTheme: full.defaultTheme || base.defaultTheme,
        exportLocale: full.exportLocale || base.exportLocale,
        apiQuotaPlanCode: full.apiQuotaPlanCode || base.apiQuotaPlanCode,
        originalApiQuotaPlanCode: full.apiQuotaPlanCode || base.apiQuotaPlanCode,
        maxOverageAmountEurosPerMonth: this.centsToEuros(full.maxOverageAmountCentsPerMonth),
        countryCode: full.countryCode || base.countryCode,
        globalIDSchemeIdentifier: full.globalIDSchemeIdentifier || base.globalIDSchemeIdentifier,
        taxRegistrations: Array.isArray(full.taxRegistrations)
          ? full.taxRegistrations.map((x) => ({
              taxRegNo: x.taxRegNo || "",
              taxSchemeID: x.taxSchemeID || "",
            }))
          : [],
      };
      this.showCompanyModal = true;
    },
    async openCopyCompany(company) {
      if (!this.canCopyCompanies) return;
      const response = await CompanyService.getById(company.id);
      if (!response?.data || !this.canCopyCompanies) return;
      this.companyIsEdit = false;
      this.companyIsCopy = true;
      this.logoFile = null;
      this.companyForm = { ...emptyCompanyForm(), ...copyCompany(response.data) };
      this.showCompanyModal = true;
    },
    openFeatureModal(company) {
      if (!this.userStore.isSystemAdmin) return;
      this.featureCompany = { id: company.id, name: company.name };
      this.featureSettings = {};
      this.featureDraft = {};
      this.featureError = "";
      this.loadCompanyFeatures(company.id);
    },
    async loadCompanyFeatures(companyId) {
      const featureCompany = this.featureCompany;
      this.featuresLoading = true;
      this.featureError = "";
      try {
        const response = await CompanyService.getFeatures(companyId);
        if (this.featureCompany !== featureCompany) return;
        this.featureSettings = Object.fromEntries((response.data.features || []).map((item) => [item.featureId, item]));
        this.featureDraft = Object.fromEntries((response.data.features || []).map((item) => [item.featureId, item.enabled]));
      } catch (_) {
        if (this.featureCompany === featureCompany) this.featureError = "Die Firmenfunktionen konnten nicht geladen werden. Bitte schließen und erneut öffnen.";
      } finally {
        if (this.featureCompany === featureCompany) this.featuresLoading = false;
      }
    },
    async saveCompanyFeatures() {
      if (!this.userStore.isSystemAdmin || !this.featureCompany || !this.hasFeatureChanges || this.featuresSaving) return;
      const companyId = this.featureCompany.id;
      const changed = this.featureDefinitions.filter(({ id }) => this.featureSettings[id]
        && this.featureDraft[id] !== this.featureSettings[id].enabled);
      this.featuresSaving = true;
      this.featureError = "";
      let savedCount = 0;
      let failedCount = 0;
      let allSaved = false;
      try {
        for (const { id } of changed) {
          try {
            const previous = this.featureSettings[id];
            const response = await CompanyService.updateFeature(companyId, id, this.featureDraft[id], previous.revision);
            this.featureSettings = { ...this.featureSettings, [id]: response.data };
            savedCount += 1;
          } catch (_) {
            failedCount += 1;
          }
        }
        if (savedCount && companyId === this.userStore.currentCompanyId) {
          this.companyFeatureStore.loadFeatures().catch(() => this.companyFeatureStore.clear());
        }
        if (failedCount) {
          this.featureError = savedCount
            ? "Einige Firmenfunktionen wurden gespeichert. Die übrigen Änderungen konnten nicht gespeichert werden."
            : "Die Firmenfunktionen konnten nicht gespeichert werden. Bitte erneut versuchen.";
        } else {
          allSaved = true;
        }
      } finally {
        this.featuresSaving = false;
        if (allSaved) this.closeFeatureModal();
      }
    },
    closeFeatureModal() {
      if (this.featuresSaving) return;
      this.featureCompany = null;
      this.featureSettings = {};
      this.featureDraft = {};
      this.featureError = "";
      this.featuresLoading = false;
    },
    closeCompanyModal() {
      this.showApiQuotaPlanConfirmModal = false;
      this.apiQuotaPlanChangePreview = null;
      this.pendingApiQuotaDowngrade = null;
      this.showCompanyModal = false;
    },
    onLogoSelected(event) {
      this.logoFile = event?.target?.files?.[0] || null;
    },
    async uploadLogo() {
      if (!this.logoFile) return;

      const formData = new FormData();
      formData.append("file", this.logoFile);

      this.uxStore.startLoading();
      try {
        const response = await apiClient.post("/file/upload?fileKind=COMPANYLOGO", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const fileId = response?.data?.id || null;
        if (fileId) {
          this.companyForm.logoFileId = fileId;
        }
      } finally {
        this.uxStore.stopLoading();
        this.logoFile = null;
        const input = document.getElementById("companyLogoFile");
        if (input) input.value = "";
      }
    },
    removeLogo() {
      this.companyForm.logoFileId = null;
    },
    addCompanyTaxReg() {
      this.companyForm.taxRegistrations.push({ taxRegNo: "", taxSchemeID: "" });
    },
    removeCompanyTaxReg(idx) {
      this.companyForm.taxRegistrations.splice(idx, 1);
    },
    async saveCompany() {
      if (!this.canWriteCompanies || this.companySaving) return;
      this.companySaving = true;
      try {
        const payload = {
          companyNumber: this.companyForm.companyNumber || null,
          isDefault: this.canSetDefaultCompany ? !!this.companyForm.isDefault : this.companyIsEdit ? undefined : false,
          name: this.companyForm.name,
          logoFileId: this.companyForm.logoFileId || null,
          defaultTheme: this.companyForm.defaultTheme || null,
          exportLocale: this.companyForm.exportLocale || null,
          apiQuotaEnabled: !!this.companyForm.apiQuotaEnabled,
          maxOverageAmountCentsPerMonth: this.eurosToCents(this.companyForm.maxOverageAmountEurosPerMonth),
          street: this.companyForm.street || null,
          postcode: this.companyForm.postcode || null,
          city: this.companyForm.city || null,
          countryCode: this.companyForm.countryCode || null,
          globalID: this.companyForm.globalID || null,
          globalIDSchemeIdentifier: this.companyForm.globalIDSchemeIdentifier || null,
          electronicAddress: this.companyForm.electronicAddress || null,
          iban: this.companyForm.iban || null,
          bic: this.companyForm.bic || null,
          bankName: this.companyForm.bankName || null,
          contactName: this.companyForm.contactName || null,
          contactOrgUnit: this.companyForm.contactOrgUnit || null,
          contactEmailAddress: this.companyForm.contactEmailAddress || null,
          contactPhoneNo: this.companyForm.contactPhoneNo || null,
          taxRegistrations: (this.companyForm.taxRegistrations || []).filter((x) => x.taxRegNo && x.taxRegNo.trim().length > 0),
        };

        if (this.companyIsEdit) {
          await CompanyService.update(this.companyForm.id, payload);
        } else {
          await CompanyService.create(payload);
        }

        this.closeCompanyModal();
        await this.loadCompanies();

        // If the active company was updated (logo/theme), refresh session data for navbar and theming.
        if (this.userStore.currentCompanyId && this.userStore.currentCompanyId === this.companyForm.id) {
          try {
            await this.userStore.loadMe();
            this.userStore.applyTheme();
          } catch (_) {
            // ignore
          }
        }

        // Refresh selected company if edited/created default selection changed.
        if (this.selectedCompanyId) {
          const refreshed = this.companies.find((c) => c.id === this.selectedCompanyId);
          if (refreshed) {
            await this.selectCompany(refreshed);
          }
        }
      } finally {
        this.companySaving = false;
      }
    },
    cancelApiQuotaPlanChange() {
      this.apiQuotaPlanChangeForm.requestedPlanCode = this.companyForm.originalApiQuotaPlanCode || "Flex";
      this.apiQuotaPlanChangePreview = null;
      this.pendingApiQuotaDowngrade = null;
      this.showApiQuotaPlanConfirmModal = false;
    },
    async openApiQuotaPlanChange() {
      if (!this.companyForm.id) return;
      await this.loadActivePendingDowngradePlan();
      this.apiQuotaPlanChangeForm.requestedPlanCode = this.companyForm.apiQuotaPlanCode || "Flex";
      this.apiQuotaPlanChangePreview = null;
      this.showApiQuotaPlanConfirmModal = true;
      await this.previewApiQuotaPlanChange();
    },
    async previewApiQuotaPlanChange() {
      if (!this.companyForm.id || !this.apiQuotaPlanChangeForm.requestedPlanCode) return;
      const response = await CompanyService.previewApiPlanChange(
        this.companyForm.id,
        this.apiQuotaPlanChangeForm.requestedPlanCode,
      );
      this.apiQuotaPlanChangePreview = response?.data || null;
      this.pendingApiQuotaDowngrade = this.apiQuotaPlanChangePreview?.existingPendingDowngrade || this.pendingApiQuotaDowngrade;
    },
    async confirmApiQuotaPlanChange() {
      const response = await CompanyService.applyApiPlanChange(
        this.companyForm.id,
        this.apiQuotaPlanChangeForm.requestedPlanCode,
      );
      const preview = response?.data?.preview || this.apiQuotaPlanChangePreview;
      if (preview?.requestedPlanCode && preview?.changeKind === "Upgrade") {
        this.companyForm.apiQuotaPlanCode = preview.requestedPlanCode;
        this.companyForm.originalApiQuotaPlanCode = preview.requestedPlanCode;
      }
      this.showApiQuotaPlanConfirmModal = false;
      this.apiQuotaPlanChangePreview = null;
      await this.loadActivePendingDowngradePlan();
      await this.loadCompanies();
    },
    async setDefaultCompany(company) {
      if (!this.canSetDefaultCompany) return;
      await CompanyService.setDefault(company.id);
      await this.loadCompanies();
      const refreshed = this.companies.find((c) => c.id === company.id);
      if (refreshed) {
        await this.selectCompany(refreshed);
      }
    },
    async deleteCompany(company) {
      if (!this.canDeleteCompanies) return;
      const confirmed = window.confirm(`Firma "${company.name}" wirklich löschen?`);
      if (!confirmed) return;
      await CompanyService.remove(company.id);
      if (this.selectedCompanyId === company.id) {
        this.selectedCompanyId = "";
        this.selectedCompany = null;
      }
      await this.loadCompanies();
    },
    openCreatePaymentTerm() {
      if (!this.selectedCompanyId || !this.canWritePaymentTerms) return;
      this.paymentTermIsEdit = false;
      this.paymentTermIsCopy = false;
      this.paymentTermForm = emptyPaymentTermForm();
      this.showPaymentTermModal = true;
    },
    openEditPaymentTerm(term) {
      if (!this.canWritePaymentTerms) return;
      this.paymentTermIsEdit = true;
      this.paymentTermIsCopy = false;
      this.paymentTermForm = { ...emptyPaymentTermForm(), ...term };
      this.showPaymentTermModal = true;
    },
    openCopyPaymentTerm(term) {
      if (!this.selectedCompanyId || !this.canCopyPaymentTerms) return;
      this.paymentTermIsEdit = false;
      this.paymentTermIsCopy = true;
      this.paymentTermForm = { ...emptyPaymentTermForm(), ...copyPaymentTerm(term) };
      this.showPaymentTermModal = true;
    },
    closePaymentTermModal() {
      this.showPaymentTermModal = false;
    },
    async savePaymentTerm() {
      if (!this.selectedCompanyId || !this.canWritePaymentTerms || this.paymentTermSaving) return;
      this.paymentTermSaving = true;
      try {

        const payload = {
          name: this.paymentTermForm.name,
          daysUntilDue: this.paymentTermForm.daysUntilDue || 0,
          textTemplate: this.paymentTermForm.textTemplate || null,
          isDefault: this.canSetDefaultPaymentTerm ? !!this.paymentTermForm.isDefault : this.paymentTermIsEdit ? undefined : false,
        };

        if (this.paymentTermIsEdit) {
          await CompanyService.updatePaymentTerm(this.selectedCompanyId, this.paymentTermForm.id, payload);
        } else {
          await CompanyService.createPaymentTerm(this.selectedCompanyId, payload);
        }

        this.closePaymentTermModal();
        await this.refreshSelectedCompany();
      } finally {
        this.paymentTermSaving = false;
      }
    },
    async deletePaymentTerm(term) {
      if (!this.selectedCompanyId || !this.canDeletePaymentTerms) return;
      const confirmed = window.confirm(`Zahlungsbedingung "${term.name}" wirklich löschen?`);
      if (!confirmed) return;
      await CompanyService.removePaymentTerm(this.selectedCompanyId, term.id);
      await this.refreshSelectedCompany();
    },
    async setDefaultPaymentTerm(term) {
      if (!this.selectedCompanyId || !this.canSetDefaultPaymentTerm) return;
      await CompanyService.setDefaultPaymentTerm(this.selectedCompanyId, term.id);
      await this.refreshSelectedCompany();
    },
    async refreshSelectedCompany() {
      if (!this.selectedCompanyId) return;
      const response = await CompanyService.getById(this.selectedCompanyId);
      this.selectedCompany = response?.data || null;
    },
    async onCompaniesImported() { await this.loadCompanies(); await this.refreshSelectedCompany(); },
    async exportCompanies() {
      if (!this.canExportCompanies) return;
      const response = await CompanyService.export("json");
      const data = response?.data;
      if (!data?.fileId) {
        return;
      }

      await FileTemplateService.downloadFile(data.fileId, data.fileName || "companies");
    },
  },
  async mounted() {
    const sessionPromise = this.userStore.me
      ? Promise.resolve()
      : this.userStore.loadMe().catch((error) => {
          console.error("Failed to load /user/me:", error);
        });
    await this.loadApiQuotaPlans();
    await this.loadCompanies();

    // Keep the admin modules consistent with the invoice module: use masterdata-backed dropdowns.
    this.masterDataStore.fetchCountryCodes();
    this.masterDataStore.fetchGlobalIdSchemeIdentifiers();
    this.masterDataStore.fetchTaxRegistrationSchemeIds();

    await sessionPromise;
    await this.loadCompanies();
  },
};
</script>
